import { Injectable } from '@angular/core';
import {
  BillingApiService,
  CanCreateSubscriptionsRequestInterface,
  ListProductSummariesRequestInterface,
  ProductSummary,
  SubscribeValidationStatus,
  ValidateResponseInterface,
} from '@galaxy/billing';
import { getFrequencyValueForEdition } from '@vendasta/billing-ui';
import { Observable, combineLatest } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Pricing, TieredPrice } from '../../common/table-pricing/interface';
import { buildPricingString } from '../../common/table-pricing/utils';
import { PartnerService } from '../../core/partner.service';
import { ActivationService } from '../core/activation.service';
import { ActivatableItem } from '../core/interface';
import { OrderConfirmationItem } from './interface';

export interface FormattedTierPrice {
  tierRange: string;
  tierPrice: string;
}
export interface OrderBreakdownItem {
  productName: string;
  pricing: Pricing;
  tierPricing: FormattedTierPrice[];
  activations: number;
  totalCost: number;
  orderItem: OrderConfirmationItem;
  frequency: string;
}

export interface OrderSummary {
  orderItems: OrderBreakdownItem[];
  orderCostFrequency: Map<string, any>;
}

@Injectable()
export class StairstepOrderService {
  private productSummaries$: Observable<ProductSummary[]>;
  private activatableItems$: Observable<ActivatableItem[]>;
  private orderValidationResponse$: Observable<ValidateResponseInterface[]>;

  constructor(
    private activationService: ActivationService,
    private billingService: BillingApiService,
    private partnerService: PartnerService,
  ) {
    this.activatableItems$ = this.activationService.activatableItems$;

    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
    const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59);
    const pageSize = 1000;

    const productSummariesRequest: ListProductSummariesRequestInterface = {
      merchantId: this.partnerService.partnerId,
      startDate: startDate,
      endDate: endDate,
      pageSize: pageSize,
    };

    this.productSummaries$ = this.billingService
      .listProductSummaries(productSummariesRequest)
      .pipe(map((response) => response.results));
  }

  buildSubscriptionRequest(orderItems: OrderConfirmationItem[]): CanCreateSubscriptionsRequestInterface {
    const skuInput = {};
    orderItems
      .filter((orderItem) => orderItem.addonId === undefined || orderItem.addonId === null)
      .forEach((appItem) => {
        skuInput[this.buildProductSKU(appItem)] = appItem.quantity;
      });

    return {
      merchantId: this.partnerService.partnerId,
      skus: skuInput,
    };
  }

  orderRequiresBillingInfo(orderItems$: Observable<OrderConfirmationItem[]>): Observable<boolean> {
    return orderItems$.pipe(
      switchMap((items) =>
        this.billingService.canCreateSubscriptions(this.buildSubscriptionRequest(items)).pipe(
          map((response) => {
            const stairStepStatus = SubscribeValidationStatus.SUBSCRIBE_VALIDATION_STATUS_WILL_CAUSE_FUTURE_CHARGES;
            const validResponses =
              response.validationResponse !== undefined
                ? response.validationResponse.filter(
                    (validationResponse) => validationResponse.code.status === stairStepStatus,
                  )
                : [];
            return validResponses.length > 0;
          }),
        ),
      ),
    );
  }

  getOrderBreakDown(orderItems: OrderConfirmationItem[]): Observable<OrderSummary> {
    const skuInput = {};
    orderItems
      .filter((orderItem) => orderItem.addonId === undefined || orderItem.addonId === null)
      .forEach((appItem) => {
        skuInput[this.buildProductSKU(appItem)] = appItem.quantity;
      });

    const subscriptionCheckRequest: CanCreateSubscriptionsRequestInterface = {
      merchantId: this.partnerService.partnerId,
      skus: skuInput,
    };

    this.orderValidationResponse$ = this.billingService
      .canCreateSubscriptions(subscriptionCheckRequest)
      .pipe(map((response) => response.validationResponse));

    return combineLatest([this.productSummaries$, this.activatableItems$, this.orderValidationResponse$]).pipe(
      map(([productSummaries, activatableItems, validationResponses]) => {
        return this.buildOrderBreakDownFromValidationResponse(
          orderItems,
          productSummaries,
          activatableItems,
          validationResponses,
        );
      }),
    );
  }

  buildOrderBreakDownFromValidationResponse(
    orderItems: OrderConfirmationItem[],
    productSummaries: ProductSummary[],
    activatableItems: ActivatableItem[],
    validationResponses: ValidateResponseInterface[],
  ): OrderSummary {
    const orderBreakDown: OrderBreakdownItem[] = [];
    const orderFreqTotals: Map<string, any> = new Map([
      ['one_time', 0],
      ['monthly', 0],
      ['yearly', 0],
    ]);

    const stairStepStatus = SubscribeValidationStatus.SUBSCRIBE_VALIDATION_STATUS_WILL_CAUSE_FUTURE_CHARGES;
    if (validationResponses !== undefined && validationResponses.length > 0) {
      validationResponses
        .filter((validationResponse) => validationResponse.code.status === stairStepStatus)
        .forEach((validationResponse) => {
          const orderItem = orderItems.find((i) => this.buildProductSKU(i) === validationResponse.sku);
          const activatableItem = activatableItems.find(
            (i) => i.appId === this.buildAppIdFromSku(validationResponse.sku),
          );

          const productSku = this.buildProductSKU(orderItem);
          const productSummary = productSummaries.find((i) => i.sku === productSku);
          const tierPricing = this.getPricingForOrderItem(orderItem, activatableItem);

          const item = orderItem;
          const itemPricing = activatableItem.pricing;
          const itemTierPricing = this.buildTierPricing(tierPricing);
          const itemActivations = productSummary
            ? productSummary.totalQuantity + orderItem.quantity
            : orderItem.quantity;
          const itemTotalCost = this.getStepTierPriceForOrderItem(
            tierPricing.frequency,
            tierPricing.prices,
            productSummary,
          );

          if (itemTotalCost <= 0) {
            return;
          }

          orderFreqTotals.set(
            tierPricing.frequency.toLowerCase(),
            orderFreqTotals.get(tierPricing.frequency.toLowerCase()) + itemTotalCost,
          );

          const currentItem: OrderBreakdownItem = {
            productName: item.name,
            pricing: itemPricing,
            tierPricing: itemTierPricing,
            activations: itemActivations,
            totalCost: itemTotalCost,
            orderItem: item,
            frequency: getFrequencyValueForEdition(
              item.appPrices.pricesForContexts['wholesale'],
              item.partnerCurrency,
              item.editionId || '',
            ),
          };

          orderBreakDown.push(currentItem);
        });
    }
    return { orderItems: orderBreakDown, orderCostFrequency: orderFreqTotals };
  }

  buildProductSKU(orderItem: OrderConfirmationItem): string {
    let productSku = orderItem.appId;
    if (orderItem.editionId !== null && orderItem.editionId !== '') {
      productSku = productSku + ':' + orderItem.editionId;
    }

    return productSku;
  }

  getPricingForOrderItem(orderItem: OrderConfirmationItem, activatableItem: ActivatableItem): Pricing {
    let itemPricing: Pricing;

    if (!activatableItem.editionData.usesEditions) {
      return activatableItem.pricing;
    }

    const orderEdition = activatableItem.editionData.editions.filter((edition) => {
      const orderEditionId = orderItem.editionId === null ? '' : orderItem.editionId;
      return edition.editionId === orderEditionId;
    });
    if (orderEdition.length > 0) {
      itemPricing = orderEdition[0].pricing;
    }
    return itemPricing;
  }

  buildRangeString(min: number, max: number): string {
    const quantityMax = max && max >= 0 ? String(max) : '∞';
    const quantityMin = min && min >= 0 ? String(min) : '0';

    if (max && max < 0) {
      return `${quantityMin} and over`;
    }

    return `${quantityMin} to ${quantityMax}`;
  }

  buildTierPricing(pricing: Pricing): FormattedTierPrice[] {
    const tierPrices: FormattedTierPrice[] = [];

    pricing.prices.forEach((tier) => {
      const range = this.buildRangeString(tier.quantityMin, tier.quantityMax);
      const price = buildPricingString(tier, tier.price, pricing.currency, pricing.frequency);
      tierPrices.push({ tierRange: range, tierPrice: price });
    });

    return tierPrices;
  }

  getStepTierPriceForOrderItem(
    itemTierFrequency: string,
    itemTierPricing: TieredPrice[],
    productSummary: ProductSummary,
  ): number {
    if (
      !(productSummary === undefined || productSummary === null) &&
      !(productSummary.totalAmount === undefined || productSummary.totalAmount === null)
    ) {
      return productSummary.totalAmount;
    }

    let itemStepTierPrice = 0;
    const estimatedQuantity =
      productSummary === undefined ||
      productSummary === null ||
      productSummary.totalQuantity === undefined ||
      productSummary.totalQuantity === null
        ? 1
        : productSummary.totalQuantity + 1;

    if (itemTierPricing) {
      itemTierPricing.forEach((price) => {
        const computedMaxQuantity: number = price.quantityMax < 0 ? Number.MAX_SAFE_INTEGER : price.quantityMax;
        if (estimatedQuantity <= computedMaxQuantity && estimatedQuantity >= price.quantityMin) {
          itemStepTierPrice = price.price;
        }
      });
    }
    return itemStepTierPrice;
  }

  getOrderItemsCurrency(orderItems: OrderConfirmationItem[]): string {
    let result = '';
    if (orderItems.length > 0) {
      result = orderItems[0].partnerCurrency;
      while (result === null || result === undefined) {
        result = orderItems[0].partnerCurrency;
      }
    }
    return result;
  }

  buildAppIdFromSku(sku: string): string {
    return sku.split(':')[0];
  }
}
